import React, { useEffect, useState } from 'react';
import { MapPin, Check } from 'lucide-react';
import { authGet } from '@/utils/authFetch';

/**
 * Compact scope bar for the live customer dashboard. Shows the manager the
 * locations they've been assigned by admin (RBAC via /my-locations) and lets
 * them focus by state. Renders nothing when the business has no locations set
 * up (so demo / single-site dashboards stay clean).
 */
export default function LocationScopeBar({ businessId }) {
  const [data, setData] = useState(null);
  const [activeStates, setActiveStates] = useState(null); // null = all

  useEffect(() => {
    let alive = true;
    const fetchScoped = async (attempt = 0) => {
      if (!businessId || !alive) return;
      try {
        const res = await authGet(`/api/location-setup/${businessId}/my-locations`);
        const d = await res.json();
        if (alive) setData(d);
      } catch (e) {
        // transient abort during the dashboard's heavy mount — retry a couple times
        if (alive && attempt < 3) setTimeout(() => fetchScoped(attempt + 1), 1200);
      }
    };
    fetchScoped();
    return () => { alive = false; };
  }, [businessId]);

  if (!data || !data.locations || data.locations.length === 0) return null;

  const states = data.states || [];
  const isAll = activeStates === null;
  const shown = isAll ? data.locations : data.locations.filter((l) => activeStates.has(l.state));
  const scopedLabel = data.scope === 'assigned' ? 'Your sites' : 'All sites';

  const toggleState = (st) => {
    setActiveStates((cur) => {
      const next = new Set(cur === null ? states : Array.from(cur));
      next.has(st) ? next.delete(st) : next.add(st);
      return next.size === states.length ? null : next;
    });
  };

  return (
    <div data-testid="location-scope-bar"
      className="rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
      <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <MapPin className="w-4 h-4 text-indigo-600" />
        {scopedLabel}
        <span className="text-slate-400 font-normal">· {shown.length} of {data.locations.length}</span>
      </span>
      {states.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setActiveStates(null)} data-testid="scope-all"
            className={`text-[11px] px-2.5 py-1 rounded-full ${isAll ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
            All states
          </button>
          {states.map((st) => {
            const on = isAll || activeStates.has(st);
            return (
              <button key={st} onClick={() => toggleState(st)} data-testid={`scope-state-${st}`}
                className={`text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1 ${on ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                {on && <Check className="w-3 h-3" />} {st}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
